/**
 * src/utils/appBadge.js
 * ─────────────────────────────────────────────────────────────────────────────
 * PWA Home Screen app-icon badge utility — wraps the W3C Badging API.
 *
 * API surface
 *   updateAppBadge(count)  Set the badge to `count`.  Clears when count ≤ 0.
 *   clearAppBadge()        Explicitly clear the badge (all items read).
 *
 * Platform notes
 *   • Chrome/Edge 81+: fully supported — numeric badges on the desktop/Android.
 *   • iOS/iPadOS 16.4+: supported for Home Screen web apps only, and requires
 *     notification permission.  If permission is denied the calls silently fail.
 *     We intentionally do NOT request permission here — the existing push-
 *     notification flow in MobileTutorSettings.js handles that.
 *   • Firefox / unsupported browsers: feature-detected, no-ops, no errors.
 *   • All calls are wrapped in try/catch + .catch() so they never throw.
 *
 * Future: to combine announcement + message + alert counts, just call
 *   updateAppBadge(announcementCount + messageCount + alertCount);
 * from TutorDashboard (or wherever counts are aggregated).
 */

/**
 * Update the app-icon badge.
 * @param {number} count  Unread count.  Pass 0 (or omit) to clear.
 */
export function updateAppBadge(count) {
  const n = Number.isFinite(count) ? Math.max(0, Math.floor(count)) : 0;

  try {
    if (n === 0) {
      // Prefer the dedicated clear API; fall back to setAppBadge(0).
      if ("clearAppBadge" in navigator) {
        navigator.clearAppBadge().catch(() => {});
      } else if ("setAppBadge" in navigator) {
        navigator.setAppBadge(0).catch(() => {});
      }
    } else {
      if ("setAppBadge" in navigator) {
        navigator.setAppBadge(n).catch(() => {});
      }
    }
  } catch (_) {
    // Silently ignore — API may be unavailable or restricted.
  }
}

/**
 * Explicitly clear the badge (e.g., when all announcements are read).
 * Safe no-op when the API is unavailable.
 */
export function clearAppBadge() {
  updateAppBadge(0);
}
